import React, { useState } from "react";
import {
  HelpCircle,
  Mail,
  Phone,
  Clock,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Globe,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import { API_URL } from "../../config";
import { motion } from "framer-motion";

export default function Support() {
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactRole, setContactRole] = useState("Placement Cell Officer");
  const [contactCategory, setContactCategory] = useState("Technical");
  const [contactDesc, setContactDesc] = useState("");
  const [urgency, setUrgency] = useState("Low");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(null);
  const [formError, setFormError] = useState(null);

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setTicketSuccess(null);

    if (
      !contactName.trim() ||
      !contactEmail.trim() ||
      !contactDesc.trim() ||
      !contactRole ||
      !contactCategory ||
      !urgency
    ) {
      setFormError("Please fill in all required ticketing fields.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      contactName: contactName.trim(),
      contactEmail: contactEmail.trim(),
      contactRole: contactRole,
      contactCategory: contactCategory,
      contactDesc: contactDesc.trim(),
      urgency: urgency,
    };

    try {
      console.log("Support ticket payload:", payload);

      const response = await axios.post(
        `${API_URL}/api/freshment_portal_apis/GenericAPI/submit_support_request`,
        payload,
      );

      console.log("API response:", response);

      if (response.data.success) {
        console.log("Success:", response.data);

        setIsSubmitting(false);
        setTicketSuccess(
          `Support Ticket submitted successfully. Our team will contact you in under 24 hours.`,
        );
        setContactName("");
        setContactEmail("");
        setContactDesc("");
        setContactRole("Placement Cell Officer");
        setContactCategory("Technical");
        setUrgency("Low");
      } else {
        console.log("API returned success=false");
        setIsSubmitting(false);
        setFormError(response.data.message || "Failed to submit ticket.");
      }
    } catch (error) {
      console.log("Error:", error);
      setIsSubmitting(false);
      setFormError("Failed to submit ticket. Please try again.");
    }
  };

  return (
    <div className="position-relative overflow-hidden" style={styles.page}>
      {/* Grid overlay background */}
      <div className="position-absolute" style={styles.gridOverlay} />

      {/* Premium Decorative Lighting Circles */}
      <div className="position-absolute" style={styles.glowLeft} />
      <div className="position-absolute" style={styles.glowRight} />

      {/* Main Header */}
      <div
        className="text-center mx-auto position-relative"
        style={styles.header}
      >
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="d-inline-flex font-mono align-items-center text-uppercase"
          style={styles.badge}
        >
          <HelpCircle
            style={{ width: "16px", height: "16px", color: "#FF5F14" }}
          />
          EduConnectPro HelpDesk
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display"
          style={styles.h1}
        >
          How Can We Help <br />
          <span style={{ color: "#FF5F14" }}>You Succeed?</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={styles.headerText}
        >
          Raise priority SLA-guaranteed support tickets or get in direct contact
          with our dedicated university implementation operations desk.
        </motion.p>
      </div>

      {/* Split Layout: Ticket Creation & Redesigned Coordinates */}
      <div
        className="container d-flex flex-xl-row flex-column position-relative align-items-start"
        style={styles.contentRow}
      >
        {/* Left Column: Ticket Creation Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="col-12 col-xl-7 formCard position-relative overflow-hidden bg-white shadow"
          style={styles.formCard}
        >
          <div
            className="position-absolute formTopBar top-0 start-0 end-0"
            style={styles.formTopBar}
          />

          <div className="mb-4">
            <h3 className="fw-bold font-display" style={styles.formHeading}>
              Raise Support Ticket
            </h3>
            <p className="fw-bold mt-2" style={styles.formSubheading}>
              Our integration team responds to all validated tickets in under 24
              hours.
            </p>
          </div>

          <form
            onSubmit={handleTicketSubmit}
            className="d-flex flex-column gap-4"
            style={{ fontSize: "0.8125rem" }}
          >
            {/* Custom Error Alerts */}
            {formError && (
              <div
                className="d-flex align-items-center gap-2 fw-bold rounded-3"
                style={styles.errorAlert}
              >
                <AlertCircle
                  style={{ width: "16px", height: "16px", color: "#ef4444" }}
                />
                <span>{formError}</span>
              </div>
            )}

            {/* Custom Success Alerts */}
            {ticketSuccess && (
              <div
                className="d-flex align-items-start gap-3 fw-bold rounded-3 shadow-sm"
                style={styles.successAlert}
              >
                <CheckCircle
                  style={{
                    width: "20px",
                    height: "20px",
                    color: "#059669",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                />
                <div className="d-flex flex-column gap-1">
                  {/* <p
                    className="text-uppercase fw-bold mb-0"
                    style={styles.successLabel}
                  >
                    TRANSMISSION CONFIRMED
                  </p> */}
                  <p
                    className="fw-semibold mb-0 mt-1"
                    style={styles.successText}
                  >
                    {ticketSuccess}
                  </p>
                </div>
              </div>
            )}

            <div className="row g-3">
              <div className="col-12 col-sm-6 d-flex flex-column gap-2">
                <label
                  className="text-uppercase d-block fw-bold"
                  style={styles.label}
                >
                  Your Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="e.g. Dr. Satish Nair"
                  className="w-100   shadow-sm"
                  style={styles.input}
                />
              </div>
              <div className="col-12 col-sm-6 d-flex flex-column gap-2">
                <label
                  className="text-uppercase d-block fw-bold"
                  style={styles.label}
                >
                  Institutional Email *
                </label>
                <input
                  type="email"
                  required
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="name@college.edu.in"
                  className="w-100   shadow-sm"
                  style={styles.input}
                />
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-sm-6 d-flex flex-column gap-2">
                <label
                  className="text-uppercase d-block fw-bold"
                  style={styles.label}
                >
                  Operational Role
                </label>
                <select
                  value={contactRole}
                  onChange={(e) => setContactRole(e.target.value)}
                  className="w-100   shadow-sm"
                  style={styles.select}
                >
                  <option value="Placement Cell Officer">
                    Placement Cell Officer
                  </option>
                  <option value="Dean/HOD/Principal">
                    Dean / HOD / Principal
                  </option>
                  <option value="Student Coordinator">
                    Student Coordinator
                  </option>
                  <option value="Corporate HR Recruiter">
                    Corporate HR Recruiter
                  </option>
                </select>
              </div>
              <div className="col-12 col-sm-6 d-flex flex-column gap-2">
                <label
                  className="text-uppercase d-block fw-bold"
                  style={styles.label}
                >
                  Category
                </label>
                <select
                  value={contactCategory}
                  onChange={(e) => setContactCategory(e.target.value)}
                  className="w-100   shadow-sm"
                  style={styles.select}
                >
                  <option value="Technical">Technical</option>
                  <option value="Billing">Billing</option>
                  <option value="Issue">Issue</option>
                  <option value="Feature Request">Feature Request</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>
            </div>

            <div className="d-flex flex-column gap-2">
              <label
                className="text-uppercase d-block fw-bold"
                style={styles.label}
              >
                Urgency Level
              </label>
              <div className="d-flex gap-2  " style={styles.urgencyGroup}>
                {["Low", "Medium", "High"].map((urg) => (
                  <button
                    key={urg}
                    type="button"
                    onClick={() => setUrgency(urg)}
                    className="flex-fill text-uppercase border-0 rounded-2"
                    style={{
                      ...styles.urgencyButton,
                      ...(urgency === urg
                        ? urg === "High"
                          ? styles.urgencyCritical
                          : urg === "Medium"
                            ? styles.urgencyMedium
                            : styles.urgencyLow
                        : styles.urgencyInactive),
                    }}
                  >
                    {urg}
                  </button>
                ))}
              </div>
            </div>

            <div className="d-flex flex-column gap-2">
              <label
                className="text-uppercase d-block fw-bold"
                style={styles.label}
              >
                Detailed Description *
              </label>
              <textarea
                required
                value={contactDesc}
                onChange={(e) => setContactDesc(e.target.value)}
                rows={5}
                placeholder="Describe your operational error or integration challenge in detail. Mention error codes, spreadsheet column formats, or recruiter details if applicable."
                className="w-100   shadow-sm"
                style={styles.textarea}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-100 d-flex align-items-center justify-content-center transmit_btn text-uppercase border-0 orangeBtn"
              style={{
                ...styles.submitButton,
                opacity: isSubmitting ? 0.5 : 1,
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={styles.spinner} />
                  <span>Transmitting Issue Ticket...</span>
                </>
              ) : (
                <>
                  <Send style={{ width: "16px", height: "16px" }} />
                  <span>Send Email</span>
                </>
              )}
            </button>
          </form>
        </motion.div>

        {/* Right Column: DIRECT INSTITUTION COORDINATES */}
        <div className="col-12 col-xl-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="position-relative overflow-hidden rounded-4 bg-white shadow"
            style={styles.coordCard}
          >
            {/* Top gradient visual strip */}
            <div
              className="position-absolute top-0 start-0 end-0"
              style={styles.coordTopStrip}
            />

            {/* Content header */}
            <div
              className="d-flex align-items-center justify-content-between"
              style={styles.coordHeader}
            >
              <div>
                <span
                  className="text-uppercase font-mono d-block"
                  style={styles.coordEyebrow}
                >
                  VERIFIED CHANNELS
                </span>
                <h4 className="fw-bold" style={styles.coordTitle}>
                  Direct Institution Coordinates
                </h4>
              </div>
              <div
                className="d-flex align-items-center gap-2 rounded-pill shadow-sm"
                style={styles.liveBadge}
              >
                <span className="rounded-circle" style={styles.liveDot} />
                <span
                  className="text-uppercase font-mono fw-bold"
                  style={styles.liveText}
                >
                  LIVE NOW
                </span>
              </div>
            </div>

            {/* Coordinates Body Info list */}
            <div className="d-flex flex-column gap-4" style={styles.coordBody}>
              {/* Placement Officers Hotline info */}
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 shadow-sm"
                  style={styles.coordIconBox}
                >
                  <Mail style={{ width: "20px", height: "20px" }} />
                </div>
                <div className="d-flex flex-column gap-1">
                  <p
                    className="text-uppercase fw-bold mb-0"
                    style={styles.coordItemTitle}
                  >
                    Placement Coordinator Mailbox
                  </p>
                  <p className="fw-bold mb-0" style={styles.coordItemDesc}>
                    Secure registrar & campus data inquiries
                  </p>
                  <a
                    href="mailto:contact@freshmentapp.com"
                    className="d-inline-block underline-link fw-bold text-decoration-none"
                    style={styles.coordLinkOrange}
                  >
                    contact@freshmentapp.com
                  </a>
                </div>
              </div>

              {/* Emergency Call Center info */}
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 shadow-sm"
                  style={styles.coordIconBox}
                >
                  <Phone style={{ width: "20px", height: "20px" }} />
                </div>
                <div className="d-flex flex-column gap-1">
                  <p
                    className="text-uppercase fw-bold mb-0"
                    style={styles.coordItemTitle}
                  >
                    Campus Drive Hotline
                  </p>
                  <p className="fw-bold mb-0" style={styles.coordItemDesc}>
                    Priority coordination and recruiter emergencies
                  </p>
                  <a
                    href="tel:+918355907853"
                    className="d-inline-block orange-color-hover fw-bold text-decoration-none"
                    style={styles.coordLinkDark}
                  >
                    +91 83559 07853
                  </a>
                </div>
              </div>

              {/* Coverage Hours info */}
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 shadow-sm"
                  style={styles.coordIconBox}
                >
                  <Clock style={{ width: "20px", height: "20px" }} />
                </div>
                <div className="d-flex flex-column gap-1">
                  <p
                    className="text-uppercase fw-bold mb-0"
                    style={styles.coordItemTitle}
                  >
                    Coverage & Operational SLA
                  </p>
                  <p className="fw-bold mb-0" style={styles.coordItemDesc}>
                    Standard office dispatchments & callback response
                  </p>
                  <div className="rounded-3 mt-2" style={styles.slaBox}>
                    <p className="fw-bold mb-0" style={styles.slaText}>
                      Monday — Saturday: 09:00 AM - 07:00 PM IST
                    </p>
                    <p
                      className="d-flex font-mono align-items-center text-uppercase fw-bold gap-2 mt-2 mb-0"
                      style={styles.slaSubtext}
                    >
                      <ShieldCheck style={{ width: "16px", height: "16px" }} />
                      Priority SLA channels 24/7/365 active
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer lock disclaimer */}
            <div
              className="d-flex align-items-center gap-2"
              style={styles.coordFooter}
            >
              <Globe
                style={{ width: "16px", height: "16px", color: "#94a3b8" }}
              />
              <span className="font-mono">
                Globally federated secure campus routing active
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes supportSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes supportPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .support-input:focus {
          outline: none;
          border-color: #FF5F14 !important;
          box-shadow: 0 0 0 4px rgba(255, 95, 20, 0.05) !important;
          background-color: #fff !important;
        }
        
            .formCard:hover .formTopBar{
              background-color: #FF5F14 !important;
            }

            .transmit_btn{
            GAP:10PX;
     padding: 1rem;
    font-size: 0.75rem;
            }

            @media (max-width: 480px)
            {
              .transmit_btn{
                gap:4px;
                padding-left: 0.60rem;
                padding-right: 0.60rem;
                font-size: 9px;
              }
            }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    color: "#0f172a",
    padding: "6rem 1rem",
  },
  gridOverlay: {
    inset: 0,
    backgroundImage:
      "linear-gradient(to right, #8080800a 1px, transparent 1px), linear-gradient(to bottom, #8080800a 1px, transparent 1px)",
    backgroundSize: "14px 24px",
    maskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    WebkitMaskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    pointerEvents: "none",
  },
  glowLeft: {
    top: "48px",
    left: "25%",
    width: "500px",
    height: "500px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.03)",
    filter: "blur(140px)",
    pointerEvents: "none",
  },
  glowRight: {
    bottom: "48px",
    right: "48px",
    width: "600px",
    height: "600px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 237, 213, 0.1)",
    filter: "blur(160px)",
    pointerEvents: "none",
  },
  header: {
    maxWidth: "48rem",
    marginBottom: "5rem",
    zIndex: 10,
  },
  badge: {
    gap: "0.375rem",
    padding: "0.375rem 0.875rem",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    border: "1px solid rgba(255, 237, 213, 0.6)",
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: 900,
    letterSpacing: "0.1em",
    fontFamily: "monospace",
    marginBottom: "1rem",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  h1: {
    fontSize: "2.75rem",
    fontWeight: 900,
    color: "#020617",
    letterSpacing: "-0.025em",
    marginBottom: "1rem",
    lineHeight: 1.1,
  },
  headerText: {
    color: "#475569",
    fontSize: "0.9375rem",
    lineHeight: 1.625,
    fontWeight: 600,
    maxWidth: "42rem",
    marginLeft: "auto",
    marginRight: "auto",
  },
  contentRow: {
    gap: "2.5rem",
    zIndex: 10,
  },
  formCard: {
    borderRadius: "1rem",
    padding: "2.5rem",
    boxShadow: "0 20px 25px -5px rgba(241, 245, 249, 0.4)",
  },
  formTopBar: {
    height: "4px",
    backgroundColor: "rgba(255, 95, 20, 0.1)",
  },
  formHeading: {
    fontSize: "1.5rem",
    color: "#020617",
  },
  formSubheading: {
    fontSize: "0.75rem",
    color: "#64748b",
  },
  errorAlert: {
    padding: "1rem",
    backgroundColor: "#fef2f2",
    border: "1px solid #fee2e2",
    color: "#b91c1c",
  },
  successAlert: {
    padding: "1.25rem",
    backgroundColor: "#ecfdf5",
    border: "1px solid #d1fae5",
    color: "#065f46",
  },
  successLabel: {
    fontSize: "10px",
    fontFamily: "monospace",
    letterSpacing: "0.1em",
    color: "#022c22",
  },
  successText: {
    fontSize: "0.75rem",
    color: "#047857",
    lineHeight: 1.625,
  },
  label: {
    fontSize: "0.75rem",
    color: "#334155",
    letterSpacing: "0.05em",
  },
  input: {
    padding: "0.75rem 1rem",
    borderRadius: "0.75rem",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    color: "#020617",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  select: {
    padding: "0.75rem 1rem",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    color: "#020617",
    fontSize: "0.75rem",
    fontWeight: 700,
    borderRadius: "0.75rem",
    cursor: "pointer",
  },
  urgencyGroup: {
    borderRadius: "10px",
    padding: "0.25rem",
    backgroundColor: "#f1f5f9",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    maxWidth: "24rem",
  },
  urgencyButton: {
    padding: "0.625rem",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.05em",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  urgencyLow: {
    backgroundColor: "#FF5F14",
    color: "#fff",
    boxShadow: "0 4px 6px -1px rgba(255, 95, 20, 0.1)",
  },
  urgencyMedium: {
    backgroundColor: "#f59e0b",
    color: "#020617",
    boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.1)",
  },
  urgencyCritical: {
    backgroundColor: "#ef4444",
    color: "#fff",
    boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.1)",
  },
  urgencyInactive: {
    color: "#64748b",
    backgroundColor: "transparent",
  },
  textarea: {
    padding: "0.75rem 1rem",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    color: "#020617",
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.625,
    borderRadius: "0.75rem",
    resize: "vertical",
  },
  submitButton: {
    borderRadius: "12px",
    backgroundColor: "#FF5F14",
    color: "#fff",
    fontWeight: 800,
    letterSpacing: "0.1em",
    cursor: "pointer",
    boxShadow: "0 10px 15px -3px rgba(255, 95, 20, 0.15)",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "supportSpin 0.8s linear infinite",
  },
  coordCard: {
    borderRadius: "10px",
    padding: "2rem",
    boxShadow: "0 20px 25px -5px rgba(241, 245, 249, 0.4)",
  },
  coordTopStrip: {
    height: "6px",
    background: "linear-gradient(to right, #fb923c, #FF5F14, #c2410c)",
  },
  coordHeader: {
    paddingBottom: "1.5rem",
    borderBottom: "1px solid #f1f5f9",
  },
  coordEyebrow: {
    fontSize: "10.5px",
    fontFamily: "monospace",
    fontWeight: 700,
    letterSpacing: "0.2em",
    color: "#FF5F14",
  },
  coordTitle: {
    fontSize: "1.25rem",
    color: "#0f172a",
    letterSpacing: "-0.025em",
    marginTop: "0.375rem",
  },
  liveBadge: {
    backgroundColor: "#ecfdf5",
    border: "1px solid rgba(209, 250, 229, 0.6)",
    padding: "0.25rem 0.75rem",
  },
  liveDot: {
    width: "8px",
    height: "8px",
    backgroundColor: "#10b981",
    animation: "supportPulse 2s ease-in-out infinite",
  },
  liveText: {
    fontSize: "10px",
    color: "#047857",
    letterSpacing: "0.05em",
    fontFamily: "monospace",
  },
  coordBody: {
    padding: "1.5rem 0",
  },
  coordIconBox: {
    width: "44px",
    height: "44px",
    background:
      "linear-gradient(to bottom right, #fff7ed, rgba(255, 237, 213, 0.3))",
    border: "1px solid rgba(255, 237, 213, 0.8)",
    color: "#FF5F14",
  },
  coordItemTitle: {
    fontSize: "0.75rem",
    color: "#0f172a",
    letterSpacing: "0.05em",
  },
  coordItemDesc: {
    fontSize: "0.75rem",
    color: "#64748b",
    lineHeight: 1.625,
  },
  coordLinkOrange: {
    fontSize: "0.875rem",
    color: "#FF5F14",
    fontFamily: "monospace",
    marginTop: "0.125rem",
  },
  coordLinkDark: {
    fontSize: "0.875rem",
    color: "#020617",
    fontFamily: "monospace",
    letterSpacing: "-0.025em",
    marginTop: "0.125rem",
  },
  slaBox: {
    backgroundColor: "#f8fafc",
    border: "1px solid rgba(241, 245, 249, 0.8)",
    padding: "0.875rem",
    maxWidth: "24rem",
  },
  slaText: {
    fontSize: "0.75rem",
    color: "#334155",
  },
  slaSubtext: {
    fontSize: "10px",
    color: "#059669",
    fontFamily: "monospace",
    letterSpacing: "0.05em",
  },
  coordFooter: {
    paddingTop: "1.5rem",
    borderTop: "1px solid #f1f5f9",
    fontSize: "10px",
    color: "#94a3b8",
    fontFamily: "monospace",
  },
};
